import { StudentLayout } from "@jambushrushti/components/learning/StudentLayout";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";

import {
  ClipboardList,
  Clock,
  Calendar,
  CheckCircle,
  AlertCircle,
  FileText,
  Upload,
  Eye,
  BookOpen,
} from "lucide-react";
import { format, isPast, differenceInDays } from "date-fns";

const mockAssignments: (
  | {
      id: string;
      title: string;
      course: string;
      description: string;
      dueDate: Date;
      maxScore: number;
      status: string;
      submittedAt: null;
      score: null;
      feedback?: undefined;
    }
  | {
      id: string;
      title: string;
      course: string;
      description: string;
      dueDate: Date;
      maxScore: number;
      status: string;
      submittedAt: Date;
      score: null;
      feedback?: undefined;
    }
  | {
      id: string;
      title: string;
      course: string;
      description: string;
      dueDate: Date;
      maxScore: number;
      status: string;
      submittedAt: Date;
      score: number;
      feedback: string;
    }
)[] = [];

export default function MyAssignments() {
  const pendingAssignments = mockAssignments.filter(
    a => a.status === "pending"
  );
  const submittedAssignments = mockAssignments.filter(
    a => a.status === "submitted"
  );
  const gradedAssignments = mockAssignments.filter(a => a.status === "graded");

  const getStatusBadge = (assignment: (typeof mockAssignments)[0]) => {
    if (assignment.status === "graded") {
      return (
        <Badge className="border-emerald-200 bg-emerald-500/10 text-emerald-700">
          <CheckCircle className="mr-1 h-3 w-3" />
          Graded
        </Badge>
      );
    }
    if (assignment.status === "submitted") {
      return (
        <Badge className="border-blue-200 bg-blue-500/10 text-blue-700">
          <Clock className="mr-1 h-3 w-3" />
          Under Review
        </Badge>
      );
    }
    if (isPast(assignment.dueDate)) {
      return (
        <Badge className="border-red-200 bg-red-500/10 text-red-700">
          <AlertCircle className="mr-1 h-3 w-3" />
          Overdue
        </Badge>
      );
    }
    const daysLeft = differenceInDays(assignment.dueDate, new Date());
    if (daysLeft <= 2) {
      return (
        <Badge className="border-amber-200 bg-amber-500/10 text-amber-700">
          <Clock className="mr-1 h-3 w-3" />
          Due Soon
        </Badge>
      );
    }
    return (
      <Badge className="border-gray-200 bg-gray-500/10 text-gray-700">
        <Calendar className="mr-1 h-3 w-3" />
        Pending
      </Badge>
    );
  };

  const AssignmentCard = ({
    assignment,
  }: {
    assignment: (typeof mockAssignments)[0];
  }) => (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              {getStatusBadge(assignment)}
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              {assignment.title}
            </h3>
            <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
              <BookOpen className="h-4 w-4" />
              {assignment.course}
            </p>
          </div>
          {assignment.status === "graded" && (
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">
                {assignment.score}
              </p>
              <p className="text-xs text-muted-foreground">
                / {assignment.maxScore}
              </p>
            </div>
          )}
        </div>

        <p className="mb-4 line-clamp-2 text-sm text-muted-foreground">
          {assignment.description}
        </p>

        <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            Due: {format(assignment.dueDate, "MMM d, yyyy")}
          </span>
          <span className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            Max Score: {assignment.maxScore}
          </span>
          {assignment.submittedAt && (
            <span className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Submitted: {format(assignment.submittedAt, "MMM d")}
            </span>
          )}
        </div>

        {assignment.feedback && (
          <div className="mb-4 rounded-lg bg-muted/50 p-3">
            <p className="mb-1 text-sm font-medium text-foreground">
              Instructor Feedback:
            </p>
            <p className="text-sm text-muted-foreground">
              {assignment.feedback}
            </p>
          </div>
        )}

        <div className="flex gap-2">
          {assignment.status === "pending" && (
            <Button className="flex-1">
              <Upload className="mr-2 h-4 w-4" />
              Submit Assignment
            </Button>
          )}
          {assignment.status === "submitted" && (
            <Button variant="outline" className="flex-1">
              <Eye className="mr-2 h-4 w-4" />
              View Submission
            </Button>
          )}
          {assignment.status === "graded" && (
            <Button variant="outline" className="flex-1">
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // Calculate stats
  const totalScore = gradedAssignments.reduce(
    (acc, a) => acc + (a.score || 0),
    0
  );
  const maxPossibleScore = gradedAssignments.reduce(
    (acc, a) => acc + a.maxScore,
    0
  );
  const averageScore =
    maxPossibleScore > 0
      ? Math.round((totalScore / maxPossibleScore) * 100)
      : 0;

  return (
    <StudentLayout title="My Assignments">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-amber-500/10 p-3">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {pendingAssignments.length}
                </p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-blue-500/10 p-3">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {submittedAssignments.length}
                </p>
                <p className="text-sm text-muted-foreground">Under Review</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-emerald-500/10 p-3">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {gradedAssignments.length}
                </p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-primary/10 p-3">
                <ClipboardList className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {averageScore}%
                </p>
                <p className="text-sm text-muted-foreground">Average Score</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Assignments Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="pending">
              Pending ({pendingAssignments.length})
            </TabsTrigger>
            <TabsTrigger value="submitted">
              Submitted ({submittedAssignments.length})
            </TabsTrigger>
            <TabsTrigger value="graded">
              Graded ({gradedAssignments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {pendingAssignments.length > 0 ? (
              pendingAssignments.map(assignment => (
                <AssignmentCard key={assignment.id} assignment={assignment} />
              ))
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <CheckCircle className="mx-auto mb-4 h-12 w-12 text-emerald-500" />
                  <h3 className="mb-2 text-lg font-semibold text-foreground">
                    All Caught Up!
                  </h3>
                  <p className="text-muted-foreground">
                    You have no pending assignments.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="submitted" className="space-y-4">
            {submittedAssignments.length > 0 ? (
              submittedAssignments.map(assignment => (
                <AssignmentCard key={assignment.id} assignment={assignment} />
              ))
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <h3 className="mb-2 text-lg font-semibold text-foreground">
                    No Submissions
                  </h3>
                  <p className="text-muted-foreground">
                    You haven't submitted any assignments yet.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="graded" className="space-y-4">
            {gradedAssignments.length > 0 ? (
              gradedAssignments.map(assignment => (
                <AssignmentCard key={assignment.id} assignment={assignment} />
              ))
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <ClipboardList className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <h3 className="mb-2 text-lg font-semibold text-foreground">
                    No Graded Work
                  </h3>
                  <p className="text-muted-foreground">
                    No assignments have been graded yet.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </StudentLayout>
  );
}
